// Install-gate logic. Kept framework-free and tiny: it runs on every visit before
// the app bundle is even requested.
//
// The rule: the app runs when installed (standalone display mode) or on desktop.
// A phone in a browser tab sees only the install screen. On iOS the browser tab and
// the installed app have separate storage containers — registering in a tab would
// strand the device keys — so the gate is protective, not cosmetic.

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface GateDecision {
  boot: boolean;
  platform: 'android' | 'ios' | 'other';
}

let stashedPrompt: BeforeInstallPromptEvent | null = null;

export function captureInstallPrompt(): void {
  addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    stashedPrompt = event as BeforeInstallPromptEvent;
    // The gate may already be on screen; give it its button.
    for (const button of document.querySelectorAll<HTMLButtonElement>('.gate-install')) {
      button.hidden = false;
    }
    const hint = document.querySelector<HTMLElement>('.gate-lang:not([hidden]) .gate-hint');
    if (hint) hint.hidden = true;
  });
}

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

export function isPhone(): boolean {
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (typeof nav.userAgentData?.mobile === 'boolean') {
    return nav.userAgentData.mobile;
  }
  const ua = navigator.userAgent;
  // iPadOS reports itself as Macintosh; the touch-point count gives it away.
  const iPad = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return /android|iphone|ipad|ipod/i.test(ua) || iPad;
}

function isIos(): boolean {
  const ua = navigator.userAgent;
  const iPad = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return /iphone|ipad|ipod/i.test(ua) || iPad;
}

export function decideGate(): GateDecision {
  const phone = isPhone();
  const platform: GateDecision['platform'] = phone ? (isIos() ? 'ios' : 'android') : 'other';
  // Desktop is not gated: blocking it would cost code for no benefit, and Firefox
  // on desktop cannot install web apps at all.
  return { boot: isStandalone() || !phone, platform };
}

export function showGate(decision: GateDecision): void {
  const gate = document.getElementById('gate');
  if (!gate) return;

  const lang = navigator.language.toLowerCase().startsWith('pl') ? 'pl' : 'en';
  document.documentElement.lang = lang;
  const block = gate.querySelector<HTMLElement>(`.gate-lang[data-lang="${lang}"]`);
  if (!block) return;
  block.hidden = false;

  if (decision.platform === 'ios') {
    const steps = block.querySelector<HTMLElement>('.gate-ios');
    if (steps) steps.hidden = false;
  } else {
    // Android/Chromium: a real install button once the browser offers the prompt;
    // until then a hint, replaced the moment `beforeinstallprompt` fires.
    const button = block.querySelector<HTMLButtonElement>('.gate-install');
    const hint = block.querySelector<HTMLElement>('.gate-hint');
    if (stashedPrompt && button) {
      button.hidden = false;
    } else if (hint) {
      hint.hidden = false;
    }
    button?.addEventListener('click', () => {
      void stashedPrompt?.prompt();
    });
  }

  gate.hidden = false;
}
